/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ProductCardProps } from '@coursera/cds-core';
import { Grid, ProductCard, VisuallyHidden } from '@coursera/cds-core';

import useIsBrowse from 'bundles/collections-common/utils/useIsBrowse';
import { getStatusTagRedesignExperimentVariant } from 'bundles/product-card/components/utils/getStatusTagRedesignExperimentVariant';
import {
  isInBrowseFilterTestVariant,
  isInEQPFilterTestVariant,
  isInFilterTestVariant,
} from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEnterpriseSearch from 'bundles/search-common/utils/useIsEnterpriseSearch';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';

import _t from 'i18n!nls/search-common';

const styles = {
  productCard: css`
    padding: 0 var(--cds-spacing-100) var(--cds-spacing-300);
  `,
};

type CdsCardsProps = {
  resultsPerPage: number;
  variant?: ProductCardProps['variant'];
  coachOpen?: boolean;
};

export const ProductCardV2Placeholder = ({
  variant = 'grid',
  experimentVariant,
}: {
  variant?: ProductCardProps['variant'];
  experimentVariant?: ProductCardProps['experimentVariant'];
}) => {
  const placeholderProps: ProductCardProps = {
    variant,
    productType: ' ',
    experimentVariant,
  };

  return <ProductCard {...placeholderProps} loading={true} />;
};

export const ProductsCardsCdsPlaceholder = ({ resultsPerPage, variant, coachOpen = false }: CdsCardsProps) => {
  const searchCards = Array.from({ length: resultsPerPage }, (v, i) => i);
  const isConsumerSearchPage = useIsConsumerSerp();
  const isEnterpriseSearchPage = useIsEnterpriseSearch();
  const isBrowsePage = useIsBrowse();
  const isEqpPage = useIsEqp();
  const isLearnerInFilterTestVariant =
    (isEqpPage && isInEQPFilterTestVariant()) ||
    (isBrowsePage && isInBrowseFilterTestVariant()) ||
    ((isConsumerSearchPage || isEnterpriseSearchPage) && isInFilterTestVariant());

  const desktopCardSize = React.useMemo(() => {
    let lgCardSize: 3 | 4 | 12 = 4;

    if (isLearnerInFilterTestVariant) {
      lgCardSize = 3;
    }

    if (coachOpen) {
      lgCardSize = 4;
    }

    if (variant === 'list') {
      lgCardSize = 12;
    }

    return lgCardSize;
  }, [isLearnerInFilterTestVariant, coachOpen, variant]);

  const { productCardExperimentVariant: experimentVariant } = getStatusTagRedesignExperimentVariant();

  return (
    <Grid container>
      <VisuallyHidden role="status">{_t('Loading search results')}</VisuallyHidden>

      {searchCards.map((placeholderIndex) => (
        <Grid
          key={placeholderIndex}
          item
          lg={desktopCardSize}
          md={variant === 'list' ? 12 : 4}
          sm={variant === 'list' ? 12 : 6}
          xs={12}
          css={styles.productCard}
        >
          <ProductCardV2Placeholder variant={variant} experimentVariant={experimentVariant} />
        </Grid>
      ))}
    </Grid>
  );
};
